'use client';

import { PageSkeleton } from '@/app/[locale]/components/PageSkeleton';
import {
  USER_SESSION_CACHE_TIME,
  USER_SESSION_STALE_TIME,
  useUser,
} from '@/hooks/useUser';
import { defaultCountry } from '@/i18n/i18nConfig';
import { Country } from '@/i18n/i18nConfig.types';
import type { User } from '@/server/api/schemas/user.types';
import { api } from '@/trpc/client';
import { useUser as useAuth0User } from '@auth0/nextjs-auth0/client';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

interface OfferToolUserContextValue {
  user: User | undefined;
  isLoading: boolean;
  refetch: () => void;
  getCountry: () => Country;
}

const OfferToolUserContext = createContext<OfferToolUserContextValue>({
  user: undefined,
  isLoading: true,
  refetch: () => {
    throw new Error(
      'useOfferToolUser must be used within OfferToolUserProvider',
    );
  },
  getCountry: () => defaultCountry,
});

export const OfferToolUserProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user: auth0User, isLoading: isAuth0Loading } = useAuth0User();
  const { getCurrentUser } = useUser();
  const utils = api.useUtils();
  const previousAuth0EmailRef = useRef<string | undefined>(undefined);
  const hasClearedCacheRef = useRef(false);

  const {
    data: user,
    isLoading: isUserLoading,
    refetch,
  } = getCurrentUser(undefined, {
    staleTime: USER_SESSION_STALE_TIME,
    gcTime: USER_SESSION_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Derive country from user data
  const getCountry = () =>
    user?.tenant?.country?.countryCode ??
    user?.country?.countryCode ??
    defaultCountry;

  // Clear all cached data to prevent data bleeding between users
  const clearAllCachedData = useCallback(async () => {
    if (hasClearedCacheRef.current) {
      return;
    }
    hasClearedCacheRef.current = true;

    // Invalidate all queries to clear the entire cache
    await utils.invalidate();
  }, [utils]);

  // Detect user changes and clear cache when a different user logs in
  // This prevents data bleeding when users switch on the same machine
  useEffect(() => {
    const currentAuth0Email = auth0User?.email;

    // User logged out - clear cache
    if (!auth0User && previousAuth0EmailRef.current) {
      hasClearedCacheRef.current = false;
      void clearAllCachedData();
      previousAuth0EmailRef.current = undefined;
      return;
    }

    // Different user logged in - clear cache and refetch
    if (
      currentAuth0Email &&
      previousAuth0EmailRef.current &&
      currentAuth0Email !== previousAuth0EmailRef.current
    ) {
      hasClearedCacheRef.current = false;
      void clearAllCachedData().then(() => {
        void refetch();
      });
    }

    // Update the tracked email
    if (currentAuth0Email) {
      previousAuth0EmailRef.current = currentAuth0Email;
    }
  }, [auth0User, clearAllCachedData, refetch]);

  // Verify cached user matches current Auth0 session
  // This handles bfcache scenarios where old data might be restored
  useEffect(() => {
    if (!isAuth0Loading && auth0User?.email && user?.email) {
      // Cached user doesn't match Auth0 session - data bleeding risk!
      if (user.email !== auth0User.email) {
        hasClearedCacheRef.current = false;
        void clearAllCachedData().then(() => {
          void refetch();
        });
      }
    }
  }, [
    auth0User?.email,
    user?.email,
    isAuth0Loading,
    clearAllCachedData,
    refetch,
  ]);

  // Force refetch user data when Auth0 session becomes available (after login)
  useEffect(() => {
    if (!isAuth0Loading && auth0User && !user && !isUserLoading) {
      // Auth0 session exists but user data is not loaded - force refetch
      void refetch();
    }
  }, [auth0User, user, isAuth0Loading, isUserLoading, refetch]);

  // Show loading spinner while Auth0 is loading or user data is loading
  const isInitialLoading = isAuth0Loading || (auth0User && isUserLoading);

  if (isInitialLoading) {
    return <PageSkeleton />;
  }

  // Once Auth0 is loaded, isLoading in context only reflects user loading state
  // (Auth0 loading is handled above, so by the time children render, Auth0 is ready)
  return (
    <OfferToolUserContext.Provider
      value={{
        user,
        isLoading: isUserLoading,
        /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
        refetch,
        getCountry,
      }}
    >
      {children}
    </OfferToolUserContext.Provider>
  );
};

export const useOfferToolUser = () => {
  const context = useContext(OfferToolUserContext);
  if (context === undefined) {
    throw new Error(
      'useOfferToolUser must be used within OfferToolUserProvider',
    );
  }
  return context;
};
